import { Card, Form, Grid, Spacer, toast } from '@components/common'
import { ReportsApi } from '@api'
import { FormProvider, useForm } from 'react-hook-form'
import { ERROR_REQUEST, ASSET_EXPORT_REPORT_PATH } from '@constants'
import ReportFilters from '../ReportFilters'
import { defaultReportFilterFields } from '../ReportFilters/report-filter-fields'
import { reportResponseHandler } from '@utils/reports'
import { ActionButtons } from '../General'
import { useReport } from '@hooks'
import { useEffect } from 'react'
import MemorizeReports from '../General/MemorizeReports/MemorizeReport'
import { useMemorizeReport } from '../General/MemorizeReports/useMemorizeReport'
import { useFormMethods } from '../General/useFormMethods'
import { useLocation } from 'react-router-dom'
import AssetColumnsSelector from './AssetColumnsSelector'
import { AssetExportReportParams } from '@api/models'

const defaultValues: AssetExportReportParams = {
  assetCompanyID: 0,
  filter: [],
  selectedColumns: [
    'AssetCompanyID',
    'AssetID',
    'AS_DESC',
    'AS_DESC_E',
    'AS_SERIAL',
    'AS_ADD_DAT',
    'AS_ADD_USR',
    'AS_CHG_DAT',
    'AS_CHG_USR',
    'AS_KEY0_ID',
    'AS_KEY1_ID',
    'AS_KEY2_ID',
    'AS_KEY3_ID',
    'AS_KEY4_ID',
    'AS_KEY5_ID',
    'AS_KEY6_ID',
    'AS_KEY7_ID',
    'AS_KEY8_ID',
    'AS_KEY9_ID',
    'AS_CAT_ID',
    'AS_TCLS_ID',
    'AS_PRI_UCD',
    'AS_SEC_UCD',
    'AS_TAXB_CD',
    'AS_TAXL_CD',
    'AS_TAX_BUS',
    'AS_TAX_PTP',
    'AS_REC_DAT',
    'AS_VEN_ID',
    'AS_VEN_NM',
    'AS_REC_PON',
    'AS_REC_USR',
    'AS_FIN_COS',
    'AS_FIN_SAL',
    'AS_FIN_VAL',
    'AS_FIN_CLC',
    'AS_FIN_CSC',
    'AS_FIN_COC',
    'AS_FIN_CFC',
    'AS_FIN_TAC',
    'AS_DIS_CD',
    'AS_DIS_DAT',
    'AS_DIS_SAL',
    'AS_DIS_NAM',
    'AS_IN_POL',
    'AS_REP_DAT',
    'AS_STAT_CD',
    'AS_BOK4_CD',
    'AS_WRO_CD',
    'AS_WRO_DAT',
    'AS_PAR_ASS',
    'AS_NSER_DT',
    'AS_MAIN_CN',
    'AS_DEP_STC',
    'AS_LEASE',
    'AS_LOCD_CD',
    'AS_LAPC_ID',
    'AS_LDPC_ID',
    'AS_SERD_CD',
    'AS_SAPC_ID',
    'AS_SDPC_ID',
  ],
  fileFormat: 'excel',
}

const AssetExportForm = () => {
  async function onSubmitHandler(values: AssetExportReportParams) {
    await ReportsApi.getInstance()
      .getAssetExportReport(values)
      .then(response => reportResponseHandler(response, values.fileFormat))
      .catch(() => toast.error(ERROR_REQUEST))
  }

  const location = useLocation()
  const locationState: any = location.state

  const {
    methods,
    isRetrievingTemplate,
    isEditTemplate,
    selectedTemplateDetails,
  } = useFormMethods(locationState, defaultValues)

  const {
    formState: { isSubmitting },
    setValue,
    watch,
  } = methods

  const { isLoading, isSuccess, isError, isNoCompanies, assetCompanyID } = useReport()

  const {
    handleMemorizeTemplateSubmit,
    isMemorizeTemplateModalOpen,
    closeMemorizeTemplateModal,
    triggerMemorizeTemplateModal,
    handleEditTemplateSubmit,
  } = useMemorizeReport({ methods, currentReportPath: ASSET_EXPORT_REPORT_PATH })

  const fileFormat = watch('fileFormat')

  useEffect(() => {
    if (isSuccess) {
      setValue('assetCompanyID', assetCompanyID)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, assetCompanyID])

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (isError) {
    return <div>Error</div>
  }

  if (isNoCompanies) {
    return <div>No companies found</div>
  }

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmitHandler)}>
          <Card title={'Export Parameters'}>
            <Grid>
              <Grid.Item>
                <AssetColumnsSelector />
              </Grid.Item>

              <Grid.Item>
                <ReportFilters
                  filtersTemplateData={
                    isRetrievingTemplate
                      ? locationState.templateDetails.templateData.filter
                      : []
                  }
                  filterFields={defaultReportFilterFields}
                  includesTotals={fileFormat === 'pdf'}
                />
              </Grid.Item>
            </Grid>
          </Card>
          <Spacer y="default" />
          <ActionButtons
            isSubmitting={isSubmitting}
            triggerMemorizeTemplateModal={triggerMemorizeTemplateModal}
            isEditTemplate={isEditTemplate}
            allowPdf={false}
            allowCSV={true}
            disableGenerateReportBtn={!watch('selectedColumns').length}
          />
        </Form>
      </FormProvider>

      <MemorizeReports
        isMemorizeTemplateModalOpen={isMemorizeTemplateModalOpen}
        closeMemorizeTemplateModal={closeMemorizeTemplateModal}
        handleMemorizeTemplateSubmit={handleMemorizeTemplateSubmit}
        handleEditTemplateSubmit={handleEditTemplateSubmit}
        isEditTemplate={isEditTemplate}
        selectedTemplateDetails={selectedTemplateDetails}
      />
    </>
  )
}

export default AssetExportForm
